import { PLANS, formatMoney, type Plan } from "@szerviz/core";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { ErrorText } from "../components/ErrorText";
import { useApp } from "../context";
import { BILLING_STATUSES, OP_STAT_FIGURES, OP_STAT_PLATFORM, type BillingStatus, type OpStatFigures, type OpStatsMonth, type OpStatsOverview, type OpStatsWorkshop, type OpStatTotals } from "../data";

/**
 * Operator statistics (owner's rule, 2026-11-01: as much information as possible, dense, with
 * search, filters and statistics; computer only). Period totals with the change against the
 * previous period of equal length, a sortable and filterable workshop table, and 12 month charts
 * for the platform or the workshop clicked in the table.
 */

export type PeriodKind = "thisMonth" | "lastMonth" | "last30" | "thisYear" | "custom";
const PERIODS: readonly PeriodKind[] = ["thisMonth", "lastMonth", "last30", "thisYear", "custom"];

const day = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

/** The first and last day (YYYY-MM-DD, both included) of a period, counted from today. */
export function periodRange(kind: Exclude<PeriodKind, "custom">, today = new Date()): { from: string; to: string } {
  const y = today.getFullYear();
  const m = today.getMonth();
  if (kind === "lastMonth") return { from: day(new Date(y, m - 1, 1)), to: day(new Date(y, m, 0)) };
  if (kind === "last30") return { from: day(new Date(y, m, today.getDate() - 29)), to: day(today) };
  if (kind === "thisYear") return { from: day(new Date(y, 0, 1)), to: day(today) };
  return { from: day(new Date(y, m, 1)), to: day(today) };
}

/** Days in the period (for the AI alert of a longer period). */
const daysOf = (from: string, to: string) => Math.max(1, Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1);

/** Real AI cost above the set share of the monthly fee (pro rata for the period), or AI cost with no fee. */
export function statsAiAlert(w: Pick<OpStatsWorkshop, "aiCost" | "monthlyFee">, alertShare: number, days: number): boolean {
  if (w.aiCost <= 0) return false;
  return w.monthlyFee === null || w.monthlyFee <= 0 || w.aiCost > w.monthlyFee * alertShare * (days / 30);
}

const approvalRate = (approved: number, items: number) => (items > 0 ? approved / items : null);

type SortKey =
  | "name" | "plan" | "billingStatus" | "seatsUsed" | "activeUsers" | "workOrdersCreated" | "workOrdersClosed" | "revenue"
  | "invoicesIssued" | "quotesSent" | "approval" | "aiCalls" | "aiCost" | "aiCharged" | "aiMargin" | "smsCount" | "vehiclesNew"
  | "lastActivity" | "createdAt";

/** SMS as a multiplication: "5 × 25 Ft = 125 Ft" (count × price per SMS); free ones just "5 db". */
export function smsCell(count: number, amount: number, money: (n: number) => string, num: (n: number) => string): string {
  if (count === 0) return num(0);
  if (amount === 0) return num(count);
  const unit = amount / count;
  return Number.isInteger(unit) ? `${num(count)} × ${money(unit)} = ${money(amount)}` : `${num(count)} × ≈${money(Math.round(unit))} = ${money(amount)}`;
}

/**
 * Operator statistics. With `workshopId` (the operator opened one workshop) it shows that
 * workshop's own figures, change and charts, without the workshop table.
 */
export function OperatorStats({ alertShare, onOpen, workshopId }: { alertShare: number; onOpen: (workshopId: string) => void; workshopId?: string }) {
  const { t, i18n } = useTranslation();
  const { store } = useApp();
  const [kind, setKind] = useState<PeriodKind>("last30");
  const [custom, setCustom] = useState(() => periodRange("thisMonth"));
  const range = kind === "custom" ? custom : periodRange(kind);
  const valid = !!range.from && !!range.to && range.from <= range.to;
  const [overview, setOverview] = useState<OpStatsOverview | null>(null);
  const [rows, setRows] = useState<OpStatsWorkshop[]>([]);
  const [months, setMonths] = useState<OpStatsMonth[]>([]);
  const [selected, setSelected] = useState<string | null>(workshopId ?? null);
  const [prevRows, setPrevRows] = useState<OpStatsWorkshop[]>([]);
  const [error, setError] = useState<unknown>(null);

  const [query, setQuery] = useState("");
  const [plan, setPlan] = useState<Plan | "all">("all");
  const [status, setStatus] = useState<BillingStatus | "all">("all");
  const [onlyAlert, setOnlyAlert] = useState(false);
  const [activity, setActivity] = useState<"all" | "active" | "inactive">("all");
  const [sort, setSort] = useState<{ key: SortKey; dir: 1 | -1 }>({ key: "revenue", dir: -1 });

  const money = useCallback((minor: number, currency = "HUF") => formatMoney(minor, currency, i18n.language), [i18n.language]);
  const num = (n: number) => n.toLocaleString(i18n.language);
  const pct = (r: number | null) => (r === null ? "–" : `${Math.round(r * 100)}%`);
  const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(i18n.language) : "–");
  const days = valid ? daysOf(range.from, range.to) : 30;

  useEffect(() => {
    if (!valid) return;
    let live = true;
    setError(null);
    Promise.all([store.opStatsOverview(range.from, range.to), store.opStatsWorkshops(range.from, range.to)]).then(
      async ([o, w]) => {
        if (!live) return;
        setOverview(o);
        setRows(w);
        // one workshop: its figures in the previous period too, for the change
        if (workshopId) {
          const p = await store.opStatsWorkshops(o.previous.from, o.previous.to);
          if (live) setPrevRows(p);
        }
      },
      (err) => live && setError(err),
    );
    return () => {
      live = false;
    };
  }, [store, range.from, range.to, valid, workshopId]);

  useEffect(() => {
    let live = true;
    store.opStatsMonthly(12, selected).then((m) => live && setMonths(m), (err) => live && setError(err));
    return () => {
      live = false;
    };
  }, [store, selected]);

  const q = query.trim().toLowerCase();
  const shown = useMemo(() => {
    const value = (r: OpStatsWorkshop): number | string => {
      if (sort.key === "approval") return approvalRate(r.quoteItemsApproved, r.quoteItems) ?? -1;
      if (sort.key === "plan") return PLANS.indexOf(r.plan);
      if (sort.key === "lastActivity") return r.lastActivity ?? "";
      if (sort.key === "name") return r.name.toLowerCase();
      return r[sort.key] as number | string;
    };
    return rows
      .filter((r) => !q || r.name.toLowerCase().includes(q) || (r.ownerEmail ?? "").toLowerCase().includes(q))
      .filter((r) => plan === "all" || r.plan === plan)
      .filter((r) => status === "all" || r.billingStatus === status)
      .filter((r) => !onlyAlert || statsAiAlert(r, alertShare, days))
      .filter((r) => {
        if (activity === "all") return true;
        const active = r.activeUsers > 0 || r.workOrdersCreated > 0 || r.aiCalls > 0;
        return activity === "active" ? active : !active;
      })
      .sort((a, b) => {
        const x = value(a);
        const y = value(b);
        return (x < y ? -1 : x > y ? 1 : 0) * sort.dir || a.name.localeCompare(b.name);
      });
  }, [rows, q, plan, status, onlyAlert, activity, sort, alertShare, days]);

  const selectedRow = rows.find((r) => r.workshopId === selected) ?? null;
  const sum = (k: keyof OpStatsWorkshop) => shown.reduce((s, r) => s + (typeof r[k] === "number" ? (r[k] as number) : 0), 0);

  const head = (key: SortKey, label: string, numeric = true) => (
    <th aria-sort={sort.key === key ? (sort.dir === 1 ? "ascending" : "descending") : "none"} className={numeric ? "num" : undefined}>
      <button type="button" className="sort" onClick={() => setSort((s) => ({ key, dir: s.key === key ? (s.dir === 1 ? -1 : 1) : numeric ? -1 : 1 }))}>
        {label}
        <span aria-hidden="true">{sort.key === key ? (sort.dir === 1 ? " ▲" : " ▼") : ""}</span>
      </button>
    </th>
  );

  const chip = <V extends string>(name: string, value: V, current: V, set: (v: V) => void, label: string) => (
    <label key={value} className={`ops-chip${current === value ? " selected" : ""}`}>
      <input type="radio" name={name} checked={current === value} onChange={() => set(value)} />
      {label}
    </label>
  );

  return (
    <section className="ops-stats" aria-labelledby="ops-stats-title">
      <h2 id="ops-stats-title">{t(workshopId ? "operator.stats.workshopTitle" : "operator.stats.title")}</h2>
      {!workshopId && <p className="muted">{t("operator.stats.intro")}</p>}

      <div className="ops-period" role="radiogroup" aria-label={t("operator.stats.period")}>
        {PERIODS.map((p) => chip("ops-period", p, kind, setKind, t(`operator.stats.period.${p}`)))}
        {kind === "custom" && (
          <>
            <label className="ops-date">
              <span>{t("operator.stats.from")}</span>
              <input type="date" value={custom.from} max={custom.to || undefined} onChange={(e) => setCustom({ ...custom, from: e.target.value })} aria-label={t("operator.stats.from")} />
            </label>
            <label className="ops-date">
              <span>{t("operator.stats.to")}</span>
              <input type="date" value={custom.to} min={custom.from || undefined} onChange={(e) => setCustom({ ...custom, to: e.target.value })} aria-label={t("operator.stats.to")} />
            </label>
          </>
        )}
        {overview && valid && (
          <span className="muted ops-range">
            {t("operator.stats.range", { from: date(overview.current.from), to: date(overview.current.to), prevFrom: date(overview.previous.from), prevTo: date(overview.previous.to) })}
          </span>
        )}
      </div>
      {!valid && <p className="warn-text">{t("operator.stats.badRange")}</p>}
      <ErrorText error={error} />

      {overview && !workshopId && <Tiles cur={overview.current} prev={overview.previous} money={money} num={num} pct={pct} />}
      {workshopId && overview && (
        <Tiles
          workshop
          cur={asTotals(selectedRow ?? undefined, overview?.current.from ?? "", overview?.current.to ?? "")}
          prev={asTotals(prevRows.find((r) => r.workshopId === workshopId), overview?.previous.from ?? "", overview?.previous.to ?? "")}
          money={(n) => money(n, selectedRow?.currency)}
          num={num}
          pct={pct}
        />
      )}

      <div className="ops-charts-head">
        <h3>{t("operator.stats.charts", { scope: selectedRow?.name ?? t("operator.stats.platform") })}</h3>
        {selectedRow && !workshopId && (
          <button type="button" onClick={() => setSelected(null)}>{t("operator.stats.clear")}</button>
        )}
      </div>
      <div className="ops-charts">
        <MiniChart title={t("operator.stats.chart.workOrders")} months={months} kind="bar"
          series={[{ label: t("operator.stats.series.created"), cls: "s-ink", values: months.map((m) => m.workOrders) }, { label: t("operator.stats.series.closed"), cls: "s-evo", values: months.map((m) => m.closed) }]} format={num} />
        <MiniChart title={t("operator.stats.chart.revenue")} months={months} kind="bar"
          series={[{ label: t("operator.stats.series.revenue"), cls: "s-accent", values: months.map((m) => m.revenue) }]} format={(v) => money(v, selectedRow?.currency)} />
        <MiniChart title={t("operator.stats.chart.ai")} months={months} kind="line"
          series={[{ label: t("operator.stats.series.aiCost"), cls: "s-warn", values: months.map((m) => m.aiCost + m.aiSharedCost) }, { label: t("operator.stats.series.aiCharged"), cls: "s-evo", values: months.map((m) => m.aiCharged) }]} format={(v) => money(v)} />
        <MiniChart title={t("operator.stats.chart.sms")} months={months} kind="bar"
          series={[{ label: t("operator.stats.series.sms"), cls: "s-ink", values: months.map((m) => m.smsCount) }]} format={num} />
        <MiniChart title={t("operator.stats.chart.users")} months={months} kind="line"
          series={[{ label: t("operator.stats.series.users"), cls: "s-accent", values: months.map((m) => m.activeUsers) }]} format={num} />
      </div>

      {!workshopId && (
      <>
      <h3>{t("operator.stats.tableTitle")}</h3>
      <div className="ops-filters">
        <input className="ops-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t("operator.stats.search")} aria-label={t("operator.stats.search")} />
        <span className="ops-group" role="radiogroup" aria-label={t("operator.col.plan")}>
          <span className="muted">{t("operator.col.plan")}:</span>
          {chip("ops-plan", "all", plan, setPlan, t("operator.stats.all"))}
          {PLANS.map((p) => chip("ops-plan", p, plan, setPlan, t(`plan.${p}.label`)))}
        </span>
        <span className="ops-group" role="radiogroup" aria-label={t("operator.col.status")}>
          <span className="muted">{t("operator.col.status")}:</span>
          {chip("ops-status", "all", status, setStatus, t("operator.stats.all"))}
          {BILLING_STATUSES.map((s) => chip("ops-status", s, status, setStatus, t(`billing.status.${s}`)))}
        </span>
        <span className="ops-group" role="radiogroup" aria-label={t("operator.stats.activity")}>
          <span className="muted">{t("operator.stats.activity")}:</span>
          {chip("ops-activity", "all", activity, setActivity, t("operator.stats.all"))}
          {chip("ops-activity", "active", activity, setActivity, t("operator.stats.active"))}
          {chip("ops-activity", "inactive", activity, setActivity, t("operator.stats.inactive"))}
        </span>
        <label className="ops-chip">
          <input type="checkbox" checked={onlyAlert} onChange={(e) => setOnlyAlert(e.target.checked)} />
          {t("operator.stats.onlyAlert", { share: Math.round(alertShare * 100) })}
        </label>
        <span className="muted">{t("operator.stats.shown", { shown: shown.length, total: rows.length })}</span>
      </div>

      <div className="table-wrap">
        <table className="ops-table">
          <thead>
            <tr>
              {head("name", t("operator.col.workshop"), false)}
              {head("plan", t("operator.col.plan"), false)}
              {head("billingStatus", t("operator.col.status"), false)}
              {head("seatsUsed", t("operator.stats.col.seats"))}
              {head("activeUsers", t("operator.stats.col.activeUsers"))}
              {head("workOrdersCreated", t("operator.stats.col.created"))}
              {head("workOrdersClosed", t("operator.stats.col.closed"))}
              {head("revenue", t("operator.stats.col.revenue"))}
              {head("invoicesIssued", t("operator.stats.col.invoices"))}
              {head("quotesSent", t("operator.stats.col.quotes"))}
              {head("approval", t("operator.stats.col.approval"))}
              {head("aiCalls", t("operator.stats.col.aiCalls"))}
              {head("aiCost", t("operator.stats.col.aiCost"))}
              {head("aiCharged", t("operator.stats.col.aiCharged"))}
              {head("aiMargin", t("operator.stats.col.aiMargin"))}
              {head("smsCount", t("operator.stats.col.sms"))}
              {head("vehiclesNew", t("operator.stats.col.vehicles"))}
              {head("lastActivity", t("operator.col.lastActivity"), false)}
              {head("createdAt", t("operator.stats.col.since"), false)}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => {
              const alert = statsAiAlert(r, alertShare, days);
              return (
                <tr key={r.workshopId} className={r.workshopId === selected ? "selected" : undefined} aria-selected={r.workshopId === selected}
                  onClick={() => setSelected((s) => (s === r.workshopId ? null : r.workshopId))}>
                  <td>
                    <strong>{r.name}</strong>{" "}
                    <a className="ops-open" href={`#operator/${r.workshopId}`} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onOpen(r.workshopId); }}>{t("operator.stats.open")}</a>
                    <div className="muted">{r.ownerEmail ?? "–"}</div>
                  </td>
                  <td>{t(`plan.${r.plan}.label`)}</td>
                  <td><span className={`badge billing-${r.billingStatus}`}>{t(`billing.status.${r.billingStatus}`)}</span></td>
                  <td className={`num${r.seatsUsed >= r.seatLimit ? " warn-text" : ""}`}>{r.seatLimit >= 100000 ? `${r.seatsUsed} / ∞` : `${r.seatsUsed} / ${r.seatLimit}`}</td>
                  <td className="num">{r.activeUsers}</td>
                  <td className="num">{r.workOrdersCreated}</td>
                  <td className="num">{r.workOrdersClosed}</td>
                  <td className="num">{money(r.revenue, r.currency)}</td>
                  <td className="num">{r.invoicesIssued}</td>
                  <td className="num">{r.quotesSent} / {r.quotesAnswered}</td>
                  <td className="num">{pct(approvalRate(r.quoteItemsApproved, r.quoteItems))}</td>
                  <td className="num">{r.aiCalls}</td>
                  <td className={`num${alert ? " warn-text" : ""}`} title={alert ? t("operator.ai.alert") : undefined}>{money(r.aiCost + r.aiSharedCost)}</td>
                  <td className="num">{money(r.aiCharged)}</td>
                  <td className={`num${r.aiMargin < 0 ? " neg" : ""}`}>{money(r.aiMargin)}</td>
                  <td className="num">{smsCell(r.smsCount, r.smsAmount, (n) => money(n, r.currency), num)}</td>
                  <td className="num">{r.vehiclesNew}</td>
                  <td>{date(r.lastActivity)}</td>
                  <td>{date(r.createdAt)}</td>
                </tr>
              );
            })}
          </tbody>
          {shown.length > 0 && (
            <tfoot>
              <tr>
                <th colSpan={3}>{t("operator.stats.total")}</th>
                <td className="num">{sum("seatsUsed")}</td>
                <td className="num">{sum("activeUsers")}</td>
                <td className="num">{sum("workOrdersCreated")}</td>
                <td className="num">{sum("workOrdersClosed")}</td>
                <td className="num">{money(shown.filter((r) => r.currency === "HUF").reduce((s, r) => s + r.revenue, 0))}</td>
                <td className="num">{sum("invoicesIssued")}</td>
                <td className="num">{sum("quotesSent")} / {sum("quotesAnswered")}</td>
                <td className="num">{pct(approvalRate(sum("quoteItemsApproved"), sum("quoteItems")))}</td>
                <td className="num">{sum("aiCalls")}</td>
                <td className="num">{money(sum("aiCost") + sum("aiSharedCost"))}</td>
                <td className="num">{money(sum("aiCharged"))}</td>
                <td className="num">{money(sum("aiMargin"))}</td>
                <td className="num">{smsCell(sum("smsCount"), sum("smsAmount"), (n) => money(n), num)}</td>
                <td className="num">{sum("vehiclesNew")}</td>
                <td colSpan={2} />
              </tr>
            </tfoot>
          )}
        </table>
      </div>
      {rows.length > 0 && shown.length === 0 && <p className="muted">{t("operator.none")}</p>}
      </>
      )}
    </section>
  );
}

/** Change against the previous period; for costs a rise is bad (red). */
function Delta({ cur, prev, costly = false, format }: { cur: number; prev: number; costly?: boolean; format: (n: number) => string }) {
  const { t } = useTranslation();
  const title = t("operator.stats.prev", { value: format(prev) });
  if (cur === prev) return <span className="delta flat" title={title}>= {format(prev)}</span>;
  const up = cur > prev;
  const good = up !== costly;
  const text = prev === 0 ? t("operator.stats.new") : `${Math.round(Math.abs((cur - prev) / prev) * 100)}%`;
  return (
    <span className={`delta ${good ? "good" : "bad"}`} title={title}>
      <span aria-hidden="true">{up ? "▲" : "▼"}</span> {up ? "+" : "−"}{text} <span className="muted">({format(prev)})</span>
    </span>
  );
}

/** One workshop's figures in the shape of the platform totals (platform only tiles stay 0 and are hidden). */
function asTotals(r: OpStatsWorkshop | undefined, from: string, to: string): OpStatTotals {
  const zero = Object.fromEntries([...OP_STAT_FIGURES, ...OP_STAT_PLATFORM].map((k) => [k, 0])) as unknown as OpStatTotals;
  if (!r) return { ...zero, from, to };
  const figures = Object.fromEntries(OP_STAT_FIGURES.map((k) => [k, r[k]])) as OpStatFigures;
  return { ...zero, ...figures, from, to };
}

function Tiles({ cur, prev, money, num, pct, workshop = false }: { cur: OpStatTotals; prev: OpStatTotals; money: (n: number) => string; num: (n: number) => string; pct: (r: number | null) => string; workshop?: boolean }) {
  const { t } = useTranslation();
  const tile = (key: string, value: number, before: number, format: (n: number) => string, sub?: ReactNode, costly = false) => (
    <div className="ops-tile" key={key} data-tile={key}>
      <span className="ops-tile-label">{t(`operator.stats.kpi.${key}`)}</span>
      <strong className="ops-tile-value">{format(value)}</strong>
      <Delta cur={value} prev={before} costly={costly} format={format} />
      {sub && <span className="ops-tile-sub muted">{sub}</span>}
    </div>
  );
  const aiCost = cur.aiCost + cur.aiSharedCost;
  return (
    <div className="ops-tiles" role="group" aria-label={t("operator.stats.tiles")}>
      {!workshop && tile("workshops", cur.workshopsActive, prev.workshopsActive, num, t("operator.stats.sub.workshops", { total: cur.workshopsTotal, new: cur.workshopsNew }))}
      {tile("users", cur.activeUsers, prev.activeUsers, num, t("operator.stats.sub.users", { total: cur.users }))}
      {tile("workOrders", cur.workOrdersCreated, prev.workOrdersCreated, num)}
      {tile("closed", cur.workOrdersClosed, prev.workOrdersClosed, num)}
      {tile("revenue", cur.revenue, prev.revenue, money, t("operator.stats.sub.revenue"))}
      {tile("invoices", cur.invoicesIssued, prev.invoicesIssued, num)}
      {tile("quotes", cur.quotesSent, prev.quotesSent, num, t("operator.stats.sub.quotes", { answered: cur.quotesAnswered, rate: pct(approvalRate(cur.quoteItemsApproved, cur.quoteItems)) }))}
      {tile("aiCalls", cur.aiCalls, prev.aiCalls, num)}
      {tile("aiCost", aiCost, prev.aiCost + prev.aiSharedCost, money, t("operator.stats.sub.aiCost", { shared: money(cur.aiSharedCost) }), true)}
      {tile("aiCharged", cur.aiCharged, prev.aiCharged, money)}
      {tile("aiMargin", cur.aiMargin, prev.aiMargin, money)}
      {tile("packs", cur.packsHuf, prev.packsHuf, money, t("operator.stats.sub.count", { count: cur.packsBought }))}
      {tile("sms", cur.smsCount, prev.smsCount, num, smsCell(cur.smsCount, cur.smsAmount, money, num))}
      {tile("vehicles", cur.vehiclesNew, prev.vehiclesNew, num, t("operator.stats.sub.customers", { count: cur.customersNew }))}
      {!workshop && tile("types", cur.knowledgeTypes, prev.knowledgeTypes, num)}
      {!workshop && tile("facts", cur.knowledgeFacts, prev.knowledgeFacts, num, t("operator.stats.sub.confirmed", { count: cur.knowledgeConfirmed }))}
      {!workshop && tile("votes", cur.knowledgeVotes, prev.knowledgeVotes, num)}
    </div>
  );
}

interface Series {
  label: string;
  /** colour class: s-ink, s-evo, s-accent, s-warn (brand colours from the CSS variables) */
  cls: string;
  values: number[];
}

/** A small plain SVG chart of the monthly series: grouped bars or lines, values on hover. */
export function MiniChart({ title, months, series, kind, format }: { title: string; months: OpStatsMonth[]; series: Series[]; kind: "bar" | "line"; format: (n: number) => string }) {
  const { i18n } = useTranslation();
  const W = 250;
  const H = 125;
  const pad = { l: 4, r: 4, t: 18, b: 18 };
  const n = Math.max(months.length, 1);
  const max = Math.max(1, ...series.flatMap((s) => s.values));
  const slot = (W - pad.l - pad.r) / n;
  const y = (v: number) => pad.t + (H - pad.t - pad.b) * (1 - v / max);
  const label = (m: string) => new Date(`${m}T00:00:00`).toLocaleDateString(i18n.language, { month: "short" });
  return (
    <figure className="ops-chart">
      <figcaption>
        <span>{title}</span>
        <span className="ops-legend">
          {series.map((s) => (
            <span key={s.label}><i className={s.cls} aria-hidden="true" />{s.label}</span>
          ))}
        </span>
      </figcaption>
      <svg viewBox={`0 0 ${W} ${H}`} role="img" aria-label={title}>
        <text x={pad.l} y={12} className="ops-axis">{format(max)}</text>
        <line x1={pad.l} x2={W - pad.r} y1={y(0)} y2={y(0)} className="ops-base" />
        {kind === "bar" &&
          months.map((m, i) => {
            const w = (slot * 0.76) / series.length;
            return series.map((s, k) => {
              const v = s.values[i] ?? 0;
              return (
                <rect key={`${m.month}-${k}`} className={s.cls} x={pad.l + i * slot + slot * 0.12 + k * w} y={y(v)} width={Math.max(w - 1, 1)} height={Math.max(y(0) - y(v), v > 0 ? 1 : 0)}>
                  <title>{`${label(m.month)} · ${s.label}: ${format(v)}`}</title>
                </rect>
              );
            });
          })}
        {kind === "line" &&
          series.map((s, k) => (
            <g key={k} className={s.cls}>
              <polyline fill="none" strokeWidth={2} points={s.values.map((v, i) => `${pad.l + i * slot + slot / 2},${y(v)}`).join(" ")} />
              {s.values.map((v, i) => (
                <circle key={i} cx={pad.l + i * slot + slot / 2} cy={y(v)} r={2.5}>
                  <title>{`${label(months[i]?.month ?? "")} · ${s.label}: ${format(v)}`}</title>
                </circle>
              ))}
            </g>
          ))}
        {months.map((m, i) =>
          i % 2 === n % 2 ? null : (
            <text key={m.month} x={pad.l + i * slot + slot / 2} y={H - 4} className="ops-axis" textAnchor="middle">{label(m.month)}</text>
          ),
        )}
      </svg>
    </figure>
  );
}
